import classnames from "classnames";
import noScroll from "no-scroll";
import React, { useRef, useState, useEffect } from "react";
import FocusLock from "react-focus-lock";

import { useOnClickOutside } from "../../utils/hooks";
import * as styles from "./Modal.module.css";
import Section from "../section/Section";

const Modal = ({ children, isOpen, onClose, className, ...sectionProps }) => {
  const modalRef = useRef();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      noScroll.on();
    } else {
      noScroll.off();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (!isOpen) {
        noScroll.off();
      }
    };
  }, [isOpen, onClose]);

  // Handle visibility transitions
  useEffect(() => {
    if (isOpen) {
      // When opening, first render the modal, then make it visible
      setShouldRender(true);
      // Use setTimeout to ensure the modal is rendered before making it visible
      const timeout = setTimeout(() => {
        setIsVisible(true);
      }, 0);
      
      return () => clearTimeout(timeout);
    } else {
      // When closing, first hide the modal, then remove it after transition
      setIsVisible(false);
      
      // Delay removing the modal from DOM until transition completes
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 200); // 0.2s matches CSS transition duration
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Handle click outside to close
  useOnClickOutside(modalRef, () => {
    if (isOpen) {
      onClose();
    }
  });

  const modalClasses = classnames(styles.modal, {
    [styles.modalOpen]: shouldRender,
    [styles.modalVisible]: isVisible,
  }, className);

  const content = (
    <div className={modalClasses}>
      <div className={styles.modalOverlay} />
      <div className={styles.modalContent} ref={modalRef}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close modal"
        >
          <span className={styles.closeIcon} />
        </button>
        <Section {...sectionProps} verticalCenter style={{ height: '100%' }}>
          {children}
        </Section>
      </div>
    </div>
  );

  if (shouldRender) {
    return (
      <FocusLock disabled={!isOpen}>
        {content}
      </FocusLock>
    );
  }

  return null;
};

export default Modal;
